import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface PdfGeneratorRequestPayload {
  template: string;
  useOrder?: boolean;
  locale?: string;
  data?: Record<string, unknown>;
  print?: {
    includePages?: string[];
    [key: string]: unknown;
  };
  previewFilename?: string;
  options?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PdfGeneratorProgressMessage {
  message?: string;
  socketId?: string;
  current_page?: number;
  total_pages?: number;
  progress_percent?: number;
}

export interface PdfGeneratorCompletedMessage {
  socketId?: string;
  deliveryMode?: string;
  pdfBase64?: string;
}

export interface PdfGenerationStartedEvent {
  type: 'started';
  socketId: string;
}

export interface PdfGenerationProgressEvent {
  type: 'progress';
  socketId: string;
  message: string;
  currentPage: number | null;
  totalPages: number | null;
  progressPercent: number | null;
}

export interface PdfGenerationCompletedEvent {
  type: 'completed';
  socketId: string;
  deliveryMode: string;
  pdfBase64: string;
  pdfBlob: Blob;
  pdfUrl: string;
}

export type PdfGenerationEvent =
  | PdfGenerationStartedEvent
  | PdfGenerationProgressEvent
  | PdfGenerationCompletedEvent;

interface PdfGenerationCacheEntry {
  pdfUrl: string;
  pdfBlob: Blob;
  pdfBase64: string;
}

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorSocketService {
  private static readonly MAX_PDF_CACHE_ENTRIES = 12;

  private readonly pdfGenerationCache = new Map<string, PdfGenerationCacheEntry>();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) {}

  generatePdf(
    payload: PdfGeneratorRequestPayload,
    options: { forceRefresh?: boolean } = {},
  ): Observable<PdfGenerationEvent> {
    const cacheKey = this.buildCacheKey(payload);

    if (options.forceRefresh) {
      const existing = this.pdfGenerationCache.get(cacheKey);
      if (existing) {
        this.revokePdfUrl(existing.pdfUrl);
        this.pdfGenerationCache.delete(cacheKey);
      }
    }

    const cached = this.pdfGenerationCache.get(cacheKey);

    if (cached) {
      this.pdfGenerationCache.delete(cacheKey);
      this.pdfGenerationCache.set(cacheKey, cached);

      return new Observable<PdfGenerationEvent>((subscriber) => {
        subscriber.next({
          type: 'completed',
          socketId: '',
          deliveryMode: 'cache',
          pdfBase64: cached.pdfBase64,
          pdfBlob: cached.pdfBlob,
          pdfUrl: cached.pdfUrl,
        });
        subscriber.complete();
      });
    }

    return new Observable<PdfGenerationEvent>((subscriber) => {
      const token = this.authService.getToken();
      if (!token) {
        subscriber.error(new Error('Missing bearer token for PDF generation.'));
        return undefined;
      }

      const generatorBaseUrl = this.resolveGeneratorBaseUrl();
      if (!generatorBaseUrl) {
        subscriber.error(new Error('Missing PDF generator URL in environment configuration.'));
        return undefined;
      }

      const socket = this.createSocketConnection(generatorBaseUrl, token);
      let finished = false;

      const cleanup = (): void => {
        socket.off('connect');
        socket.off('connect_error');
        socket.off('disconnect');
        socket.off('uploads');
        socket.off('pdf-generated');
        socket.disconnect();
      };

      const fail = (error: unknown): void => {
        if (finished) {
          return;
        }

        finished = true;
        cleanup();
        subscriber.error(this.normalizeError(error));
      };

      socket.on('connect_error', (error: unknown) => {
        fail(error);
      });

      socket.on('disconnect', (reason: string) => {
        if (!finished && reason !== 'io client disconnect') {
          fail(new Error(`PDF socket disconnected: ${reason}`));
        }
      });

      socket.on('uploads', (message: PdfGeneratorProgressMessage) => {
        if (!this.isCurrentSocketMessage(socket, message?.socketId)) {
          return;
        }

        const socketId = socket.id;
        if (!socketId) {
          fail(new Error('PDF generator progress arrived without a socket id.'));
          return;
        }

        subscriber.next({
          type: 'progress',
          socketId,
          message: typeof message.message === 'string' ? message.message : '',
          currentPage: this.toNullableNumber(message.current_page),
          totalPages: this.toNullableNumber(message.total_pages),
          progressPercent: this.toNullableNumber(message.progress_percent),
        });
      });

      socket.on('pdf-generated', (message: PdfGeneratorCompletedMessage) => {
        if (!this.isCurrentSocketMessage(socket, message?.socketId)) {
          return;
        }

        const socketId = socket.id;
        if (!socketId) {
          fail(new Error('PDF generator socket completed without a socket id.'));
          return;
        }

        const pdfBase64 = typeof message.pdfBase64 === 'string' ? message.pdfBase64 : '';
        if (!pdfBase64) {
          fail(new Error('PDF generator socket completed without a base64 payload.'));
          return;
        }

        const pdfBlob = this.base64ToPdfBlob(pdfBase64);
        const pdfUrl = URL.createObjectURL(pdfBlob);

        this.cachePdfGeneration(cacheKey, {
          pdfUrl,
          pdfBlob,
          pdfBase64,
        });

        finished = true;
        subscriber.next({
          type: 'completed',
          socketId,
          deliveryMode: typeof message.deliveryMode === 'string' ? message.deliveryMode : 'socket_base64',
          pdfBase64,
          pdfBlob,
          pdfUrl,
        });
        cleanup();
        subscriber.complete();
      });

      socket.on('connect', async () => {
        const socketId = socket.id;
        if (!socketId) {
          fail(new Error('PDF socket connected without a socket id.'));
          return;
        }

        subscriber.next({
          type: 'started',
          socketId,
        });

        try {
          await lastValueFrom(
            this.http.post(
              `${generatorBaseUrl}/pdf/get-pdf`,
              {
                ...payload,
                socketId,
              },
              {
                headers: new HttpHeaders({
                  Authorization: `Bearer ${token}`,
                }),
                params: new HttpParams().set('show_error', 'true'),
              },
            ),
          );
        } catch (error) {
          fail(error);
        }
      });

      socket.connect();

      return () => {
        if (!finished) {
          cleanup();
        }
      };
    });
  }

  createPdfDataUrl(pdfBase64: string): string {
    return `data:application/pdf;base64,${pdfBase64}`;
  }

  revokePdfUrl(pdfUrl: string | null | undefined): void {
    if (!pdfUrl) {
      return;
    }

    URL.revokeObjectURL(pdfUrl);
  }

  private buildCacheKey(payload: PdfGeneratorRequestPayload): string {
    return JSON.stringify(payload);
  }

  private cachePdfGeneration(cacheKey: string, entry: PdfGenerationCacheEntry): void {
    this.pdfGenerationCache.set(cacheKey, entry);

    while (this.pdfGenerationCache.size > PdfGeneratorSocketService.MAX_PDF_CACHE_ENTRIES) {
      const oldestKey = this.pdfGenerationCache.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }

      const oldest = this.pdfGenerationCache.get(oldestKey);
      this.pdfGenerationCache.delete(oldestKey);

      if (oldest) {
        this.revokePdfUrl(oldest.pdfUrl);
      }
    }
  }

  private createSocketConnection(generatorBaseUrl: string, token: string): Socket {
    return io(this.resolveSocketOrigin(generatorBaseUrl), {
      autoConnect: false,
      path: '/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      auth: {
        token,
        bearerToken: token,
        authorization: `Bearer ${token}`,
      },
    });
  }

  private resolveGeneratorBaseUrl(): string {
    return String((environment as Record<string, unknown>)['pdfGeneratorUrl'] ?? environment.pdfGeneratorUrl ?? '')
      .trim()
      .replace(/\/+$/, '');
  }

  private resolveSocketOrigin(generatorBaseUrl: string): string {
    try {
      return new URL(generatorBaseUrl).origin;
    } catch {
      return generatorBaseUrl.replace(/\/api(?:\/.*)?$/i, '');
    }
  }

  private isCurrentSocketMessage(socket: Socket, socketId: string | undefined): boolean {
    return !!socket.id && typeof socketId === 'string' && socketId === socket.id;
  }

  private toNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private base64ToPdfBlob(pdfBase64: string): Blob {
    const binary = atob(pdfBase64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: 'application/pdf' });
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string' && error.trim() !== '') {
      return new Error(error);
    }

    if (typeof error === 'object' && error !== null) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim() !== '') {
        return new Error(message);
      }
    }

    return new Error('Unknown PDF generation error.');
  }
}